import { motion } from "framer-motion";
import {
  Snowflake,
  Refrigerator,
  WashingMachine,
  Shirt,
  Wind,
  GlassWater,
  MessageCircle,
} from "lucide-react";
import { SERVICES, waLink } from "@/lib/constants";

const ICONS = { Snowflake, Refrigerator, WashingMachine, Shirt, Wind, GlassWater };

const scrollToDetail = (slug) => {
  const el = document.getElementById(slug);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Services() {
  return (
    <section id="layanan" data-testid="services-section" className="relative py-20 lg:py-40">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 lg:mb-20 flex flex-wrap items-end justify-between gap-8"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand-ink mb-4">( Layanan Kami )</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl md:text-6xl lg:text-7xl">
              Layanan service
              <br />
              <span className="text-stroke">kami</span>
            </h2>
          </div>
          <p className="hidden sm:block max-w-sm text-sm text-ink/50 leading-relaxed">
            Dari rumah tangga hingga usaha laundry — pilih layanan, chat kami, teknisi dijadwalkan ke lokasi
            Anda.
          </p>
        </motion.div>

        <div data-testid="services-mobile-grid" className="grid grid-cols-2 gap-3 lg:hidden mb-14">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon];
            return (
              <a
                key={s.slug}
                href={`#${s.slug}`}
                data-testid={`service-tile-${s.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToDetail(s.slug);
                }}
                className="flex flex-col items-start gap-3 rounded-2xl border border-ink/10 bg-white p-4 text-left shadow-[0_2px_12px_rgba(11,18,32,0.05)] active:scale-[0.97] transition-transform duration-150"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/15 text-brand-ink">
                  <Icon size={22} />
                </span>
                <span className="font-display font-bold text-sm leading-tight">{s.title}</span>
                <span className="text-[11px] text-ink/45 leading-snug">{s.short}</span>
              </a>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <motion.article
                key={s.slug}
                id={s.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                data-testid={`service-card-${s.slug}`}
                className="group flex flex-col scroll-mt-24 rounded-2xl lg:rounded-none border border-ink/10 bg-white overflow-hidden hover:border-brand transition-colors duration-500"
              >
                <div className="overflow-hidden aspect-[16/10]">
                  <img
                    src={s.img}
                    alt={`${s.title} Sanjaya Service Jakarta`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6 lg:p-7">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand-ink">
                      <Icon size={20} />
                    </span>
                    <h3 className="font-display font-extrabold uppercase tracking-tight text-lg lg:text-xl">{s.title}</h3>
                  </div>
                  <p className="mt-4 text-sm text-ink/55 leading-relaxed">{s.desc}</p>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">
                    Masalah yang ditangani
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.problems.map((p) => (
                      <span key={p} className="rounded-full border border-ink/10 px-3 py-1 text-xs text-ink/60">
                        {p}
                      </span>
                    ))}
                  </div>
                  <a
                    href={waLink(s.waText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-order-${s.slug}`}
                    aria-label={`Pesan ${s.title} via WhatsApp`}
                    className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-brand px-6 py-3 font-display font-bold text-sm text-black hover:bg-ink hover:text-white transition-colors duration-300"
                  >
                    <MessageCircle size={16} strokeWidth={2.5} />
                    Pesan Sekarang
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
